import { useCallback, useEffect, useMemo, useState } from "react";
import { Eye, RefreshCw, Search } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { DateInput } from "@/components/ui/DateInput";
import { cn } from "@/utils/cn";
import { formatCurrency, formatDateTime } from "@/utils/format";
import {
  STATUS_LABELS,
  listSolicitacoesArquivadas,
  type SolicitacaoPagamentoDto,
} from "@/services/solicitacaoPagamento";
import { SolicitacaoPagamentoDetailModal } from "./SolicitacaoPagamentoDetailModal";
import {
  EMPTY_ARQUIVADAS_FILTERS,
  filterSolicitacoesArquivadas,
  type ArquivadasFilterValues,
} from "./arquivadasFilters";

interface Props {
  open: boolean;
  onClose: () => void;
}

function ArquivadaCard({
  item,
  onRefresh,
}: {
  item: SolicitacaoPagamentoDto;
  onRefresh: () => void;
}) {
  const [detailOpen, setDetailOpen] = useState(false);

  return (
    <>
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 flex-1 min-w-0 text-sm">
          <div className="min-w-[120px]">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 block">
              Solicitante
            </span>
            <span className="text-slate-800 font-medium truncate block">{item.solicitanteName}</span>
          </div>
          <div className="min-w-[120px] flex-1">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 block">
              Fornecedor
            </span>
            <span className="text-slate-800 truncate block">{item.creditorName}</span>
          </div>
          <div className="min-w-[100px]">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 block">
              Cancelada em
            </span>
            <span className="text-slate-700 whitespace-nowrap">
              {formatDateTime(item.canceladaAt ?? item.updatedAt)}
            </span>
          </div>
          <div className="min-w-[90px]">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 block">
              Valor
            </span>
            <span className="text-slate-800 font-semibold">{formatCurrency(item.valor)}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 ml-auto">
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
            {STATUS_LABELS.cancelada}
          </span>
          <button
            type="button"
            onClick={() => setDetailOpen(true)}
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-slate-500 hover:text-brand-700 hover:bg-brand-50 transition-colors"
            title="Ver detalhes"
            aria-label="Ver detalhes da solicitação arquivada"
          >
            <Eye className="w-4 h-4" />
          </button>
        </div>
      </div>

      <SolicitacaoPagamentoDetailModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        item={item}
        mode="arquivadas"
        onAction={onRefresh}
      />
    </>
  );
}

export function SolicitacaoPagamentoArquivadasModal({ open, onClose }: Props) {
  const [items, setItems] = useState<SolicitacaoPagamentoDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ArquivadasFilterValues>(EMPTY_ARQUIVADAS_FILTERS);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listSolicitacoesArquivadas();
      setItems(data);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Erro ao carregar solicitações arquivadas.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      void load();
    } else {
      setFilters(EMPTY_ARQUIVADAS_FILTERS);
    }
  }, [open, load]);

  const filtered = useMemo(
    () => filterSolicitacoesArquivadas(items, filters),
    [items, filters],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Solicitações arquivadas"
      size="full"
      footer={
        <div className="flex justify-end">
          <Button type="button" variant="secondary" onClick={onClose}>
            Fechar
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 min-h-0">
        <div className="flex flex-col lg:flex-row lg:items-end gap-3">
          <div className="flex-1 min-w-0">
            <Input
              label="Pesquisar"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              placeholder="Solicitante, fornecedor, descrição, valor..."
              leftIcon={<Search className="w-4 h-4" />}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:w-auto lg:min-w-[320px]">
            <DateInput
              label="Data inicial"
              value={filters.dateFrom}
              onChange={(dateFrom) => setFilters((f) => ({ ...f, dateFrom }))}
            />
            <DateInput
              label="Data final"
              value={filters.dateTo}
              onChange={(dateTo) => setFilters((f) => ({ ...f, dateTo }))}
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void load()}
            loading={loading}
            leftIcon={<RefreshCw className="w-4 h-4" />}
            className="lg:mb-0.5 self-end"
          >
            Atualizar
          </Button>
        </div>

        {(filters.search || filters.dateFrom || filters.dateTo) && (
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-xs text-slate-500">
              {filtered.length} de {items.length}{" "}
              {items.length === 1 ? "solicitação" : "solicitações"}
            </p>
            <button
              type="button"
              onClick={() => setFilters(EMPTY_ARQUIVADAS_FILTERS)}
              className="text-xs font-medium text-brand-600 hover:text-brand-700"
            >
              Limpar filtros
            </button>
          </div>
        )}

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        <div
          className={cn(
            "rounded-xl border border-slate-200 bg-slate-50/50 min-h-[200px]",
            filtered.length > 0 && "max-h-[min(60vh,520px)] overflow-y-auto",
          )}
        >
          <div className="p-3 space-y-2">
            {loading && items.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">Carregando...</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">
                {items.length === 0
                  ? "Nenhuma solicitação cancelada encontrada."
                  : "Nenhuma solicitação corresponde aos filtros."}
              </p>
            ) : (
              filtered.map((item) => (
                <ArquivadaCard key={item.id} item={item} onRefresh={load} />
              ))
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
